import { mapStopToWriteStop } from '#api/adapters/stopAdapter.ts';
import type { ChargingStop } from '#domain/ChargingStop.ts';
import type { TourStop } from '#domain/TourStop.ts';
import { mapStopTypeToStop } from '#domain/TourStop.ts';

// Times are planning estimates anchored to the preceding visit, not a rescheduled customer time window.
export const acceptChargingSuggestions = (stops: TourStop[], suggestions: ChargingStop[]): TourStop[] => {
    if (stops.some(stop => stop.chargingDraftId)) {
        throw new Error('Suggestions already accepted');
    }
    const added = new Set<string>();
    const result = stops.flatMap((stop, index) => {
        let previousCharges = 0;
        const drafts = suggestions
            .filter(item => item.afterStopIndex === index)
            .map(item => {
                const anchor = Date.parse(stop.end || stop.start);
                if (
                    !Number.isFinite(anchor) ||
                    item.secondsFromPreviousStop === undefined ||
                    item.chargingSeconds === undefined ||
                    item.setupSeconds === undefined ||
                    !item.batteryCapacity ||
                    !item.calculatedAt ||
                    added.has(item.id)
                ) {
                    throw new Error('Incomplete charging suggestion');
                }
                added.add(item.id);
                const start = anchor + (item.secondsFromPreviousStop + previousCharges) * 1000;
                const duration = item.chargingSeconds + item.setupSeconds;
                if (duration <= 0 || item.targetCharge <= 0) {
                    throw new Error('Invalid charging duration');
                }
                previousCharges += duration;
                const id = `charging-draft-${crypto.randomUUID()}`;
                return {
                    id,
                    chargingDraftId: id,
                    orderId: id,
                    type: 'execute' as const,
                    serviceOrderCategory: 'charging' as const,
                    name: (item.name || 'Charging stop').slice(0, 255),
                    customer: '',
                    group: stops.length + added.size,
                    start: new Date(start).toISOString(),
                    end: new Date(start + duration * 1000).toISOString(),
                    latitude: String(item.position.lat),
                    longitude: String(item.position.lng),
                    location: { street: '', city: '', country: '', zipCode: '' },
                    charging: {
                        source: 'here' as const,
                        configuration: {
                            mode: 'target_percentage' as const,
                            target_percentage: Math.min(100, (item.targetCharge / item.batteryCapacity) * 100),
                            connector_type: 'iec62196Type2Combo' as const,
                        },
                        estimate: {
                            arrival_charge_kwh: item.arrivalCharge,
                            departure_charge_kwh: item.targetCharge,
                            battery_capacity_kwh: item.batteryCapacity,
                            charging_seconds: item.chargingSeconds,
                            setup_seconds: item.setupSeconds,
                            calculated_at: item.calculatedAt,
                            provider: 'here' as const,
                        },
                    },
                };
            });
        return [stop, ...drafts];
    });
    if (added.size !== suggestions.length) {
        throw new Error('Unmatched charging suggestion');
    }
    return result;
};

export const toChargingDraft = (stop: TourStop) => {
    if (!stop.chargingDraftId) {
        throw new Error('Missing charging draft ID');
    }
    return { client_id: stop.chargingDraftId, execution_stop: mapStopToWriteStop(mapStopTypeToStop(stop)) };
};
